/* ==========================================================================
   LONAMY RC — script commun (06/10/2026)
   - Rails : flèches sur ordinateur, points + glisser sur mobile   [data-rc-rail]
   - Apparition au défilement                                       .rc-reveal
   - Ajout au panier + ouverture du tiroir FullStack                [data-rc-ajout] / RC.ajouter()
   - Onglets simples                                                [data-rc-onglets]
   Les composants s'équipent par attribut data-rc-*, jamais par classe du thème.
   ========================================================================== */
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

const RC = (window.RC = window.RC || {});

/* ---------- Rails ---------- */
function equiperRail(rail) {
  if (rail.dataset.rcPret) return;
  rail.dataset.rcPret = '1';
  const piste = rail.querySelector('.rc-rail__piste');
  if (!piste) return;
  const prec = rail.querySelector('.rc-rail__fleche--prec');
  const suiv = rail.querySelector('.rc-rail__fleche--suiv');
  const points = rail.querySelector('.rc-rail__points');
  const pas = () => {
    const el = piste.children[0];
    if (!el) return piste.clientWidth;
    const gap = parseFloat(getComputedStyle(piste).columnGap) || 0;
    return el.getBoundingClientRect().width + gap;
  };
  const visibles = () => Math.max(1, Math.round(piste.clientWidth / pas()));
  const pages = () => Math.max(1, piste.children.length - visibles() + 1);
  const index = () => Math.round(piste.scrollLeft / pas());
  function maj() {
    const max = piste.scrollWidth - piste.clientWidth - 2;
    if (prec) prec.disabled = piste.scrollLeft <= 2;
    if (suiv) suiv.disabled = piste.scrollLeft >= max;
    const rien = max <= 0;
    if (prec) prec.hidden = rien;
    if (suiv) suiv.hidden = rien;
    if (points) {
      const n = pages();
      if (points.children.length !== n) {
        points.innerHTML = '';
        for (let i = 0; i < n; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', `Aller à l'élément ${i + 1}`);
          b.addEventListener('click', () => piste.scrollTo({ left: i * pas() }));
          points.appendChild(b);
        }
      }
      points.hidden = n <= 1;
      const k = Math.min(index(), n - 1);
      [...points.children].forEach((b, i) => b.classList.toggle('is-on', i === k));
    }
  }
  prec?.addEventListener('click', () => piste.scrollBy({ left: -pas() * visibles() }));
  suiv?.addEventListener('click', () => piste.scrollBy({ left: pas() * visibles() }));
  let t;
  piste.addEventListener('scroll', () => { cancelAnimationFrame(t); t = requestAnimationFrame(maj); }, { passive: true });
  new ResizeObserver(maj).observe(piste);
  maj();
}

/* ---------- Apparition au défilement ---------- */
const vu = 'IntersectionObserver' in window
  ? new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-vu'); vu.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px' })
  : null;

/* ---------- Onglets ---------- */
function equiperOnglets(bloc) {
  if (bloc.dataset.rcPret) return;
  bloc.dataset.rcPret = '1';
  const boutons = bloc.querySelectorAll('[role="tab"]');
  boutons.forEach((b) => b.addEventListener('click', () => {
    boutons.forEach((x) => {
      const on = x === b;
      x.setAttribute('aria-selected', on);
      const p = bloc.querySelector('#' + x.getAttribute('aria-controls'));
      if (p) p.hidden = !on;
    });
  }));
}

/* ---------- Panier ---------- */
RC.ajouter = async function (variantId, quantite = 1, bouton) {
  const tiroir = document.querySelector('cart-drawer');
  const corps = { items: [{ id: Number(variantId), quantity: Number(quantite) || 1 }] };
  if (tiroir?.dataset.sectionId) { corps.sections = [tiroir.dataset.sectionId]; corps.sections_url = window.location.pathname; }
  bouton?.classList.add('is-loading');
  try {
    const r = await fetch((window.Theme?.routes?.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(corps),
    });
    const data = await r.json();
    if (!r.ok || data.status) throw new Error(data.description || data.message || 'Ajout impossible');
    const panier = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    document.dispatchEvent(new CartUpdatedEvent(panier, { source: 'rc', itemCount: panier.item_count, variantId: String(variantId), sections: data.sections }));
    document.querySelectorAll('[data-rc-compteur]').forEach((el) => { el.textContent = panier.item_count; el.hidden = panier.item_count === 0; });
    if (tiroir) document.dispatchEvent(new Event(ThemeEvents.cartOpen));
    else window.location.href = '/cart';
    if (bouton) { bouton.classList.add('is-ok'); setTimeout(() => bouton.classList.remove('is-ok'), 1600); }
    return panier;
  } catch (e) {
    console.warn('[RC] ajout au panier :', e.message);
    if (bouton) { bouton.setAttribute('title', e.message); }
    return null;
  } finally {
    bouton?.classList.remove('is-loading');
  }
};

document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-rc-ajout]');
  if (!b) return;
  e.preventDefault();
  RC.ajouter(b.dataset.rcAjout, b.dataset.rcQte || 1, b);
});

/* Le compteur de l'en-tête suit toutes les mises à jour du panier, y compris celles du thème. */
document.addEventListener(ThemeEvents.cartUpdated, (e) => {
  const n = e.detail?.resource?.item_count;
  if (typeof n !== 'number') return;
  document.querySelectorAll('[data-rc-compteur]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
});

/* ---------- Démarrage (et rechargement de section dans l'éditeur) ---------- */
function equiper(racine = document) {
  racine.querySelectorAll('[data-rc-rail]').forEach(equiperRail);
  racine.querySelectorAll('[data-rc-onglets]').forEach(equiperOnglets);
  racine.querySelectorAll('.rc-reveal:not(.is-vu)').forEach((el) => (vu ? vu.observe(el) : el.classList.add('is-vu')));
}
RC.equiper = equiper;
equiper();
document.addEventListener('shopify:section:load', (e) => equiper(e.target));
